import React from "react";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { getAwards } from "../../data/awards";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

gsap.registerPlugin(ScrollTrigger);
function Awards() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [awards, setAwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const gridRef = useRef(null);

  const handleClick = () => {
    navigate("/awards-and-recognitions");
    window.scrollTo(0, 0);
  };
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false,
    });
  }, []);

  // Grid animation effect
  useEffect(() => {
    if (sectionRef.current && gridRef.current) {
      // Create animated grid lines
      const gridLines = gridRef.current.querySelectorAll(".grid-line");

      gridLines.forEach((line, index) => {
        gsap.fromTo(
          line,
          {
            scaleX: 0,
            opacity: 0.3,
          },
          {
            scaleX: 1,
            opacity: 0.6,
            duration: 1.5,
            ease: "power2.out",
            delay: index * 0.1,
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top 80%",
              end: "bottom 20%",
              toggleActions: "play none none reverse",
              scrub: 1,
            },
          }
        );
      });

      // Continuous grid movement
      gsap.to(gridLines, {
        x: "100%",
        duration: 20,
        ease: "none",
        repeat: -1,
        yoyo: true,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top bottom",
          end: "bottom top",
          scrub: 0.5,
        },
      });
    }
  }, [awards]);

  // Fetch awards data
  useEffect(() => {
    const fetchAwards = async () => {
      try {
        const awardsData = await getAwards();
        console.log("Fetched awards:", awardsData.length, awardsData);
        setAwards(awardsData);
      } catch (error) {
        console.error("Error fetching awards:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAwards();
  }, []);

  // Select only the first four awards
  const displayAwards = awards.slice(0, 4);
  console.log("Display awards:", displayAwards.length, displayAwards);

  // Total slides to display based on screen width
  const slidesPerView =
    window.innerWidth < 640 ? 1 : window.innerWidth < 768 ? 1.3 : 2.5;

  // Handle touch start
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  // Handle touch end
  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  // Handle touch end and determine swipe direction
  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      // Swipe left
      setCurrentSlide((prevSlide) => (prevSlide + 1) % displayAwards.length);
    }

    if (touchEnd - touchStart > 50) {
      // Swipe right
      setCurrentSlide((prevSlide) =>
        prevSlide === 0 ? displayAwards.length - 1 : prevSlide - 1
      );
    }
  };

  if (loading) {
    return (
      <div className="bg-white py-5 md:py-12 xl:py-16 relative overflow-hidden">
        <div className="container pt-5 sm:pt-8 md:pt-4 lg:pt-12 xl:pt-12 mx-auto px-3 md:px-0">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading awards...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={sectionRef}
      className="bg-white py-5 md:py-12 xl:py-16 relative overflow-hidden"
    >
      {/* Animated Grid Background */}
      <div ref={gridRef} className="absolute inset-0 pointer-events-none">
        {/* Horizontal Grid Lines - Reduced from 20 to 8 */}
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={`h-${i}`}
            className="grid-line absolute w-full h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"
            style={{
              top: `${i * 12.5}%`,
              transformOrigin: "left center",
            }}
          />
        ))}
        {/* Vertical Grid Lines - Reduced from 15 to 6 */}
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={`v-${i}`}
            className="grid-line absolute h-full w-px bg-gradient-to-b from-transparent via-gray-300 to-transparent"
            style={{
              left: `${i * 16.67}%`,
              transformOrigin: "top center",
            }}
          />
        ))}
      </div>

      <div className="container pt-5 sm:pt-8 md:pt-4 lg:pt-12 xl:pt-12 mx-auto px-3 md:px-0 relative z-10">
        {/* Mobile Layout: Heading, description, and button above cards */}
        <div className="block lg:hidden mb-8">
          <div className="flex flex-col items-center justify-center">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-medium max-w-[80vw] sm:max-w-[60vw] text-center">
              Awards & Recognitions{" "}
            </h2>

            <p className="text-sm sm:text-base md:text-lg text-gray-600 text-center mt-4 max-w-[70vw] sm:max-w-[50vw]">
              Celebrating our achievements and industry recognition for
              excellence in logistics and customer service
            </p>

            <button
              onClick={handleClick}
              className="hover:bg-primaryBlue hover:text-white border-2 font-medium
                border-primaryBlue bg-skyblue text-primaryBlue p-1 px-3 md:p-2 md:px-5 cursor-pointer transform transition-transform duration-200 hover:scale-105 rounded-full text-base md:text-lg mt-6"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              View All
            </button>
          </div>
        </div>

        <div className="relative min-h-[400px] sm:min-h-[500px] md:min-h-[600px] flex items-center justify-center">
          {/* Desktop Layout: Centered heading and button */}
          <div className="hidden lg:flex flex-col items-center justify-center z-20">
            <h2 className="text-4xl 2xl:text-5xl font-medium max-w-[40vw] text-center">
              Awards & Recognitions{" "}
            </h2>

            <p className="text-xl text-gray-600 text-center mt-4 max-w-[35vw]">
              Celebrating our achievements and industry recognition for
              excellence in logistics and customer service
            </p>

            <button
              onClick={handleClick}
              className="hover:bg-primaryBlue hover:text-white border-2 font-medium
                border-primaryBlue bg-skyblue text-primaryBlue p-2 px-5 cursor-pointer transform transition-transform duration-200 hover:scale-105 rounded-full text-lg xl:text-xl mt-6"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              View All
            </button>
          </div>

          {/* Randomly positioned award cards */}
          {displayAwards.map((award, index) => {
            // Random positions around the heading - adjusted for larger desktop cards
            const positions = [
              { top: "5%", left: "2%", transform: "rotate(-5deg)" },
              { top: "15%", right: "2%", transform: "rotate(8deg)" },
              { bottom: "20%", left: "5%", transform: "rotate(-3deg)" },
              { bottom: "5%", right: "5%", transform: "rotate(4deg)" },
            ];

            console.log(`Card ${index + 1} position:`, positions[index]);

            const position = positions[index] || { top: "50%", left: "50%" };

            return (
              <div
                key={index}
                onClick={handleClick}
                className="absolute cursor-pointer w-32 sm:w-40 md:w-48 lg:w-64 xl:w-72 h-32 sm:h-40 md:h-48 lg:h-64 xl:h-72 transform transition-all duration-300 hover:scale-110 hover:rotate-0 hover:z-30"
                style={{
                  ...position,
                  transform: position.transform,
                }}
                data-aos="fade-up"
                data-aos-delay={`${index * 200}`}
              >
                <div className="relative w-full h-full overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl">
                  {award.images && award.images[0] && (
                    <>
                      <img
                        src={award.images[0].img}
                        alt={award.images[0].alt}
                        className="w-full h-full object-cover rounded-2xl"
                      />
                      <div className="absolute bottom-0 left-0 w-full h-full bg-gradient-to-t from-black via-black/70 to-transparent opacity-90 transition-opacity duration-300 flex items-end p-2 sm:p-3 rounded-2xl">
                        <div className="flex flex-col">
                          <h3 className="text-xs sm:text-sm font-semibold text-peach">
                            {award.year}
                          </h3>
                          <h3 className="text-sm sm:text-base font-normal text-white leading-tight">
                            {award.title}
                          </h3>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Awards;
